import {
  ApprovalInboxTransaction,
  ApprovalInboxTransactionsResponse,
  UserRolesResponse,
  TransactionTypesResponse,
  AccountPortalTransactionType,
} from 'vopay';
import { JSX, ReactNode, createContext, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import useFetchUserRoles from '../hooks/useApi/useFetchUserRoles';
import useFetchApprovalTransactionsInbox from '../hooks/useApi/useFetchApprovalTransactionsInbox';
import useFetchApprovalTransactions from '../hooks/useApi/useFetchApprovalTransactions';
import useFetchWorkflows from '../hooks/useApi/useFetchWorkflows';
import useFetchTransactionTypes from '../hooks/useApi/useFetchTransactionTypes';
import { useClientData } from './ClientData.provider';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type ApprovalWorkflow = {
  allApprovalTransactions: ApprovalInboxTransaction[];
  approvalTransactions: ApprovalInboxTransactionsResponse;
  hasTransactionsRequiringApproval: boolean;
  roles: string[];
  isApprover: boolean;
  hasInvalidApprover: boolean;
  hasInvalidWorkflows: boolean;
  usersReset: () => void;
  transactionsReset: () => void;
  approvalListReset: () => void;
  areEnoughApprovers: (
    minimumAmount?: number,
    transactionTypes?: string[],
    requiredApprovers?: number,
    userRoles?: UserRolesResponse,
  ) => boolean;
  getPaymentRail: (
    transactionTypeID: AccountPortalTransactionType['ID'],
    transactionTypes?: TransactionTypesResponse,
  ) => string[];
};

export const ApprovalWorkflowContext = createContext<ApprovalWorkflow>({
  allApprovalTransactions: [],
  approvalTransactions: {
    Success: false,
    ErrorMessage: '',
    Transactions: {},
  },
  hasTransactionsRequiringApproval: false,
  roles: [],
  isApprover: false,
  hasInvalidApprover: false,
  hasInvalidWorkflows: false,
  usersReset: () => {},
  transactionsReset: () => {},
  approvalListReset: () => {},
  areEnoughApprovers: () => false,
  getPaymentRail: () => [],
});

export const ApprovalWorkflowProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { response: userRolesResponse, fetchUserRoles: getUserRoles } = useFetchUserRoles();
  const [approvalTransactionsList, getApprovalTransactions] = useFetchApprovalTransactionsInbox({});
  const [allApprovalTransactionsList, getAllApprovalTransactions] = useFetchApprovalTransactions({});
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const { workflows, fetchWorkflows } = useFetchWorkflows();
  const { response } = useClientData();
  const [approvalTransactions, setApprovalTransactions] = useState<ApprovalInboxTransactionsResponse>({
    Success: false,
    ErrorMessage: '',
    Transactions: {},
  });
  const [allApprovalTransactions, setAllApprovalTransactions] = useState<ApprovalInboxTransaction[]>([]);
  const [isGetUserRoles, setGetUserRoles] = useState(false);
  const [isGetAprrovalTrasactions, setGetAprrovalTrasactions] = useState(false);
  const [isGetAllAprrovalTrasactions, setAllGetAprrovalTrasactions] = useState(false);
  const [isGetWorkflows, setGetWorkflows] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [hasTransactionsRequiringApproval, setHasTransactionsRequiringApproval] = useState(false);
  const [hasInvalidWorkflows, setHasInvalidWorkflows] = useState(false);
  const [hasInvalidApprover, setHasInvalidApprover] = useState(false);
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const alertDialog = useAlertDialog();

  const usersReset = () => {
    setGetUserRoles(true);
  };
  const transactionsReset = () => {
    setGetAprrovalTrasactions(true);
  };
  const timeDifference = moment().subtract(2, 'year').utc().format('YYYY-MM-DD');
  const currentDateTime = moment().utc().format('YYYY-MM-DD');
  const transactionsPeriod = {
    FromDate: timeDifference,
    ToDate: currentDateTime,
  };
  const approvalListReset = () => {
    setAllGetAprrovalTrasactions(true);
    setGetWorkflows(true);
  };
  useEffect(() => {
    if (response.Success) {
      setGetWorkflows(true);
      setAllGetAprrovalTrasactions(true);
      setGetAprrovalTrasactions(true);
      setGetUserRoles(true);
    }
  }, []);
  useEffect(() => {
    if (isGetAllAprrovalTrasactions) {
      getAllApprovalTransactions({
        StartDateTime: moment(transactionsPeriod.FromDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDateTime: moment(transactionsPeriod.ToDate).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      });
      setAllGetAprrovalTrasactions(false);
    }
  }, [isGetAllAprrovalTrasactions]);
  useEffect(() => {
    if (isGetAprrovalTrasactions) {
      getApprovalTransactions({});
      setGetAprrovalTrasactions(false);
    }
  }, [isGetAprrovalTrasactions]);

  useEffect(() => {
    if (isGetUserRoles) {
      getUserRoles({
        Role: 'approver',
      });
      setGetUserRoles(false);
    }
  }, [isGetUserRoles]);

  useEffect(() => {
    if (isGetWorkflows) {
      refreshWorkflows();

      fetchTransactionTypes({
        CountriesID: response.Country === 'CA' ? 1 : 2,
        IsTransactionApprovalSupported: 1,
      });

      setGetWorkflows(false);
    }
  }, [isGetWorkflows]);

  useEffect(() => {
    if (userRolesResponse && userRolesResponse.Success) {
      setRoles(userRolesResponse.Roles);

      setIsApprover(Object.values(userRolesResponse.Roles).includes('approver'));
    }
  }, [userRolesResponse]);
  useEffect(() => {
    if (approvalTransactionsList && approvalTransactionsList.Success) {
      setApprovalTransactions(approvalTransactionsList);

      setHasTransactionsRequiringApproval(
        Object.values(approvalTransactionsList.Transactions).some((transaction) => !transaction.Approved),
      );
    }
  }, [approvalTransactionsList]);
  useEffect(() => {
    if (allApprovalTransactionsList && allApprovalTransactionsList.Success) {
      setHasInvalidApprover(
        Object.values(allApprovalTransactionsList.Transactions).some(
          (transaction) => transaction.NumberApprovalsRequired > transaction.AvailableApprovers,
        ),
      );
      setAllApprovalTransactions(Object.values(allApprovalTransactionsList.Transactions));
    }
  }, [allApprovalTransactionsList]);

  useEffect(() => {
    const requiredApprovers = workflows.map((workflow) => {
      const { MinAmount, VIOAccountTransactionTypes_ID, NumberApprovalsRequired } = workflow;

      return areEnoughApprovers(
        MinAmount,
        getPaymentRail(VIOAccountTransactionTypes_ID, transactionTypesResponse),
        NumberApprovalsRequired,
        userRolesResponse,
      );
    });

    setHasInvalidWorkflows(requiredApprovers.includes(false));
  }, [workflows, transactionTypesResponse]);

  async function refreshWorkflows() {
    try {
      await fetchWorkflows();
    } catch (err) {
      alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
    }
  }

  return (
    <ApprovalWorkflowContext.Provider
      value={{
        allApprovalTransactions,
        approvalTransactions,
        roles,
        hasTransactionsRequiringApproval,
        isApprover,
        hasInvalidApprover,
        hasInvalidWorkflows,
        usersReset,
        approvalListReset,
        transactionsReset,
        areEnoughApprovers,
        getPaymentRail,
      }}
    >
      {children}
    </ApprovalWorkflowContext.Provider>
  );

  function areEnoughApprovers(
    minimumAmountProp?: number | string,
    transactionTypes?: string[],
    requiredApprovers?: number,
    userRoles?: UserRolesResponse,
  ) {
    if (!userRoles?.UserRolePermissions) return true;

    if (minimumAmountProp === undefined) return true;

    if (!requiredApprovers) return true;

    if (!transactionTypes) return true;

    if (transactionTypes.length === 0) return true;

    const roles = Object.values(userRoles.UserRolePermissions);
    const minimumAmount = Number(minimumAmountProp);

    for (const transactionType of transactionTypes) {
      const rolesForThisType = roles
        .filter((userRole) => {
          return userRole.TransactionType === transactionType;
        })
        .filter((userRole) => {
          return (
            userRole.MaxAmount === undefined ||
            userRole.MaxAmount === null ||
            Number(userRole.MaxAmount) >= minimumAmount
          );
        });

      if (rolesForThisType.length === 0) return false;

      if (rolesForThisType.length < Number(requiredApprovers)) return false;
    }

    return true;
  }
  function getPaymentRail(
    transactionTypeID: AccountPortalTransactionType['ID'],
    transactionTypes: TransactionTypesResponse | undefined,
  ): string[] {
    const transactionType = Object.values(transactionTypes?.TransactionTypes || {}).find(
      (transactionTypesItem) => transactionTypesItem.ID === transactionTypeID,
    );

    return transactionType?.TransactionType.split('|') || [];
  }
};

export const useApprovalWorkflow = (): ApprovalWorkflow => useContext(ApprovalWorkflowContext);
